import { isObservable, makeObservable } from 'mobx';

import EventDispatcher from './EventDispatcher';

export interface BecomeObservableOptions {
    except: ReadonlyArray<string>;
}

/**
 * The Store class is the base class for all stores in the application.
 */
export default class Store extends EventDispatcher {
    /**
     * Makes all properties on this instance observable via MobX.
     *
     * All properties and methods on the instance *and* its prototype chain
     * will be passed to `mobx#makeObservable`, except for names provided in
     * `options.except`.
     *
     * The instance must be fully initialized before this method is called.
     */
    becomeObservable(options?: BecomeObservableOptions) {
        makeAllPropertiesObservable(this, options);
    }
}

// stop collecting keys once we hit one of these prototypes.
// In all cases we should stop at Store; the others are for containment in
// case a non-instance of Store somehow gets in.
const haltProtos = [
    Store.prototype,
    EventDispatcher.prototype,
    Object.prototype,
];

// There was a hardcoded exception for 'childClass' in the old code, so I'm
// keeping it. The only class in this app with a property of this name is
// `DocumentCollection`. There it has type DocumentStoreClass<T>, and is
// supposed to be a reference to a class, but at runtime MobX probably takes
// the typeof, sees that it's a function, and tries to register it as an
// action.
//
// I'm guessing it got an exception at this level because only the most-derived
// class can call `becomeObservable` (fka `init`). This means middle classes
// can't assert which of their properties shouldn't be observed, which is one
// of the major drawbacks of this approach. This is probably the motivator
// behind MobX's decorator approach, but MobX just seems very high-friction
// when used with classes.
//
// The exception for 'constructor' comes from the original impl from
// https://gist.githubusercontent.com/stephenh/77f62941913203a871d0e284ea779fe9/raw/09e2638c4e4e344f0be08cb99e5710095f5cdef3/makeSimpleAutoObservable.ts
// probably for the same reasons.
//
// This is a deliberately simplified version of that based on how it was
// actually being used in the app.
//
// 'eventTypeToListeners' is EventDispatcher's handler registry. Observing it would
// deep-convert the handler arrays, and MobX wraps functions stored in a deep
// observable - the stored handler is then no longer === the one passed in,
// which silently breaks both addEventListener()'s dedupe and
// removeEventListener().
//
// 'becomeObservable' is excluded because a class that needs to keep one of
// its own fields out of MobX has to do it by overriding becomeObservable -
// only the most-derived class calls it, so that's the one place a base class
// can add to `except`. The override lives on a prototype below Store, where
// the walk above still collects keys, so without this MobX would wrap
// becomeObservable itself as an action.
const neverObserve: ReadonlyArray<string> = [
    'constructor',
    'childClass',
    'eventTypeToListeners',
    'becomeObservable',
];

function makeAllPropertiesObservable(
    target: any,
    options: BecomeObservableOptions | undefined
) {
    if (isObservable(target)) return;

    const annotations: Record<string, boolean> = {};
    for (
        let node = target;
        node && !haltProtos.includes(node);
        node = Object.getPrototypeOf(node)
    ) {
        for (const key of Reflect.ownKeys(node)) {
            // skipping symbols also skips the special $mobx sentinel property
            if (typeof key !== 'symbol') {
                annotations[key] = true;
            }
        }
    }

    for (const exceptSet of [options?.except, neverObserve]) {
        if (exceptSet) {
            for (const key of exceptSet) {
                annotations[key] = false;
            }
        }
    }

    makeObservable(target, annotations);
}
